// ForgotPasswordPage — the auth screen at /forgot-password. Posts an
// email address to PocketBase's `users.requestPasswordReset` endpoint
// and shows a neutral confirmation regardless of the outcome.
//
// F-6 follow-up (T-028): we deliberately do NOT reveal whether the
// email belongs to an account. PocketBase's `requestPasswordReset`
// always 204s whether or not the user exists, so the API itself is
// enumeration-safe; the UI mirrors that. We never read the server
// response, never branch on it, and never render a different message
// for "found" vs "not found". The user always sees the same "if an
// account exists, a link is on its way" copy and is sent back to the
// sign-in screen.
//
// The route is also reachable under `/:locale/forgot-password` via
// the App.tsx wiring so a shared `de/...` link renders in German.

import { useState } from "react";
import type { FormEvent, ReactElement } from "react";
import { Link, Navigate } from "react-router-dom";
import { useI18n } from "../../i18n";
import { useAuth } from "../../services/auth";
import { AuthError } from "../../services/authErrors";

export function ForgotPasswordPage(): ReactElement {
  const { t } = useI18n();
  const { requestPasswordReset, user } = useAuth();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // F-6 follow-up: the confirmation state must be a single boolean.
  // We must NOT branch on whether the email exists. The screen shown
  // after submit is identical in every case.
  const [sent, setSent] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const online = typeof navigator === "undefined" ? true : navigator.onLine;

  if (user) {
    // F-3 parity with /login: a logged-in user has nothing to reset.
    return <Navigate to="/app/workout" replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!online) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await requestPasswordReset(email);
      // Confirmation is unconditional. Do not log the email or the
      // outcome to console (F-6): even logging "submitted" leaks
      // nothing, but logging "sent" vs "not found" would.
      setSent(true);
    } catch (err) {
      if (err instanceof AuthError && err.code === "offline") {
        setSubmitError(t("auth.forgotPassword.offline"));
      } else {
        setSubmitError(t("auth.forgotPassword.error"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (sent) {
    return (
      <main
        data-testid="forgot-password-sent"
        className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col gap-6 px-6 py-12"
      >
        <header>
          <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]">
            {t("brand.name")}
          </p>
          <h1 className="font-display mt-2 text-4xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)]">
            {t("auth.forgotPassword.title")}
          </h1>
        </header>
        <p
          data-testid="forgot-password-sent-message"
          className="font-sans text-base text-[var(--color-ink-hi)]"
        >
          {t("auth.forgotPassword.sent")}
        </p>
        <Link
          to="/login"
          data-testid="forgot-password-back-to-login"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={{
            minHeight: "64px",
            fontSize: 22,
            backgroundColor: "var(--color-lime)",
            borderRadius: "var(--radius-sharp)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "none",
          }}
        >
          {t("auth.forgotPassword.backToLogin")}
        </Link>
      </main>
    );
  }

  return (
    <main
      data-testid="forgot-password-page"
      className="mx-auto flex min-h-screen w-full max-w-[420px] flex-col gap-6 px-6 py-12"
    >
      <header>
        <p className="font-subhead text-sm uppercase tracking-[0.06em] text-[var(--color-lime)]">
          {t("brand.name")}
        </p>
        <h1 className="font-display mt-2 text-4xl uppercase leading-[1.1] tracking-[0.02em] text-[var(--color-ink-hi)]">
          {t("auth.forgotPassword.title")}
        </h1>
        <p className="font-sans mt-4 text-sm text-[var(--color-ink-mute)]">
          {t("auth.forgotPassword.intro")}
        </p>
      </header>
      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-4"
        data-testid="forgot-password-form"
        aria-disabled={!online}
      >
        <label className="flex flex-col gap-1">
          <span className="font-subhead text-xs uppercase tracking-[0.06em] text-[var(--color-ink-mute)]">
            {t("auth.field.email")}
          </span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            disabled={!online || submitting}
            data-testid="forgot-password-email-input"
            className="border border-[var(--color-line-strong)] bg-[var(--color-slab)] px-3 text-[var(--color-ink-hi)]"
            style={{
              minHeight: "64px",
              borderRadius: "var(--radius-sharp)",
              fontSize: 18,
            }}
          />
        </label>
        {!online && (
          <p
            data-testid="forgot-password-offline"
            className="font-sans text-sm"
            style={{ color: "var(--color-ink-mute)" }}
          >
            {t("auth.forgotPassword.offline")}
          </p>
        )}
        {submitError && (
          <p
            data-testid="forgot-password-error"
            role="alert"
            className="font-sans text-sm"
            style={{ color: "var(--color-blood)" }}
          >
            {submitError}
          </p>
        )}
        <button
          type="submit"
          disabled={!online || submitting}
          data-testid="forgot-password-submit"
          className="font-display uppercase tracking-[0.02em] text-[var(--color-void)]"
          style={{
            minHeight: "64px",
            fontSize: 22,
            backgroundColor: "var(--color-lime)",
            borderRadius: "var(--radius-sharp)",
            opacity: !online || submitting ? 0.6 : 1,
          }}
        >
          {submitting ? t("auth.forgotPassword.sending") : t("auth.forgotPassword.submit")}
        </button>
      </form>
      <p className="font-sans text-sm text-[var(--color-ink-mute)]">
        <Link to="/login" className="text-[var(--color-lime)] underline-offset-2 hover:underline">
          {t("auth.forgotPassword.backToLogin")}
        </Link>
      </p>
    </main>
  );
}
